class TdRecentlyViewed extends HTMLElement {
  static storageKey = 'tdRecentlyViewedProducts';
  static maxStored = 24;

  static selectors = {
    wrapper: '[data-td-recently-viewed-wrapper]',
    currentProduct: '[data-td-current-product]',
    next: '[data-td-recently-viewed-next]',
    pagination: '[data-td-recently-viewed-pagination]',
    prev: '[data-td-recently-viewed-prev]',
    slider: '[data-td-recently-viewed-slider]',
  };

  connectedCallback() {
    this.sliderElement = this.querySelector(TdRecentlyViewed.selectors.slider);
    this.wrapper = this.querySelector(TdRecentlyViewed.selectors.wrapper);
    this.currentProductScript = this.querySelector(TdRecentlyViewed.selectors.currentProduct);
    this.pagination = this.querySelector(TdRecentlyViewed.selectors.pagination);
    this.prevButton = this.querySelector(TdRecentlyViewed.selectors.prev);
    this.nextButton = this.querySelector(TdRecentlyViewed.selectors.next);
    this.handleStorage = this.handleStorage.bind(this);

    this.syncCurrentProduct();
    this.render();

    window.addEventListener('storage', this.handleStorage);
  }

  disconnectedCallback() {
    window.removeEventListener('storage', this.handleStorage);
    this.destroySwiper();
  }

  handleStorage(event) {
    if (event.key !== TdRecentlyViewed.storageKey) return;
    this.render();
  }

  syncCurrentProduct() {
    const currentProduct = this.getCurrentProduct();
    if (!currentProduct) return;

    const storedProducts = this.getStoredProducts();
    const filteredProducts = storedProducts.filter((product) => product.handle !== currentProduct.handle);
    const nextProducts = [currentProduct, ...filteredProducts].slice(0, TdRecentlyViewed.maxStored);

    this.setStoredProducts(nextProducts);
  }

  getCurrentProduct() {
    if (!this.currentProductScript) return null;

    try {
      const product = JSON.parse(this.currentProductScript.textContent);
      return this.normalizeProduct(product);
    } catch (_error) {
      return null;
    }
  }

  getStoredProducts() {
    try {
      const storedValue = window.localStorage.getItem(TdRecentlyViewed.storageKey);
      if (!storedValue) return [];

      const parsedValue = JSON.parse(storedValue);
      if (!Array.isArray(parsedValue)) return [];

      return parsedValue
        .map((product) => this.normalizeProduct(product))
        .filter(Boolean);
    } catch (_error) {
      return [];
    }
  }

  setStoredProducts(products) {
    try {
      window.localStorage.setItem(TdRecentlyViewed.storageKey, JSON.stringify(products));
    } catch (_error) {
      /* localStorage can fail in private browsing or restricted environments */
    }
  }

  normalizeProduct(product) {
    if (!product || typeof product !== 'object') return null;

    const id = Number.isFinite(Number(product.id)) ? Number(product.id) : null;
    const handle = typeof product.handle === 'string' ? product.handle.trim() : '';
    const title = typeof product.title === 'string' ? product.title.trim() : '';
    const url = typeof product.url === 'string' ? product.url.trim() : '';
    const image = typeof product.image === 'string' ? product.image.trim() : '';

    if (!handle || !title || !url || !image) return null;

    return {
      id,
      handle,
      title,
      url,
      image,
      alt: typeof product.alt === 'string' ? product.alt : title,
      price: Number.isFinite(Number(product.price)) ? Number(product.price) : null,
      compareAtPrice: Number.isFinite(Number(product.compareAtPrice)) ? Number(product.compareAtPrice) : null,
    };
  }

  getProductsToRender() {
    const limit = Number.parseInt(this.dataset.limit || '4', 10);
    const currentHandle = this.dataset.currentHandle || '';

    return this.getStoredProducts()
      .filter((product) => product && product.handle !== currentHandle)
      .slice(0, limit);
  }

  render() {
    if (!this.wrapper || !this.sliderElement) {
      this.hideSection();
      return;
    }

    const products = this.getProductsToRender();

    if (products.length === 0) {
      this.destroySwiper();
      this.wrapper.innerHTML = '';
      this.hideSection();
      return;
    }

    this.wrapper.innerHTML = products.map((product) => this.renderCard(product)).join('');
    this.initializeSwiper(products.length);
    this.showSection();
  }

  hideSection() {
    this.hidden = true;
    this.setAttribute('aria-hidden', 'true');
  }

  showSection() {
    this.hidden = false;
    this.setAttribute('aria-hidden', 'false');
  }

  renderCard(product) {
    const compareMarkup =
      product.compareAtPrice && product.compareAtPrice > product.price
        ? `<span class="was_price"><span class="money">${this.formatMoney(product.compareAtPrice)}</span></span>`
        : '';

    return `
      <div class="swiper-slide td-recently-viewed__slide" data-swiper-slide data-td-recently-viewed-slide>
        <a href="${this.escapeHtml(product.url)}" class="td-product-card">
          <div class="td-product-card__image-wrapper">
            <img
              src="${this.escapeHtml(product.image)}"
              alt="${this.escapeHtml(product.alt)}"
              class="td-product-card__image"
              loading="lazy"
              width="320"
              height="400"
            >
          </div>
          <span class="td-product-card__title">${this.escapeHtml(product.title)}</span>
          <span class="price${compareMarkup ? ' sale' : ''}">
            <span class="current_price">
              <span class="money">${this.formatMoney(product.price)}</span>
            </span>
            ${compareMarkup}
          </span>
        </a>
      </div>
    `;
  }

  initializeSwiper(productCount) {
    this.destroySwiper();

    if (!this.sliderElement || productCount < 1) return;

    const slides = this.wrapper.querySelectorAll('[data-swiper-slide]');

    if (slides.length === 0) return;

    this.sliderElement.slides = slides;
    this.sliderElement.initialized = false;
    this.sliderElement.sliderOptions = this.sliderElement.applyDefaultOptions(
      this.sliderElement.getSliderOptions(),
    );
    this.sliderElement.initializeSwiper();
    this.swiper = this.sliderElement.slider;
  }

  destroySwiper() {
    if (this.swiper && typeof this.swiper.destroy === 'function') {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  formatMoney(value) {
    if (!Number.isFinite(value)) return '';

    const format = window.PXUTheme?.currency?.money_format_no_currency || '${{amount}}';

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(value, format);
    }

    return `$${(value / 100).toFixed(2)}`;
  }

  escapeHtml(value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }
}

if (!customElements.get('td-recently-viewed')) {
  customElements.define('td-recently-viewed', TdRecentlyViewed);
}
